<script setup lang="ts">
import { useAddressStore } from '@/stores/useAddressStore'
import { useConsumableStore } from '@/stores/useConsumableStore'
import { useAuthStore } from '@/stores/useAuthStore'
import { useNuxtApp } from '@nuxt/bridge/dist/runtime'
import { ref, onBeforeMount, computed } from 'vue'
import ItemPurchaseAbi from '@/assets/contracts/ItemPurchaseAbi.json'
import FtAbi from '@/assets/contracts/FtAbi.json'
import LandVolumeBanner from '@/static/banner/data/LandVolumeBanner.json'
import { RecordType } from '@/_proto/ppd_pb'
import { useWalletStore } from '@/stores/useWalletStore'
const TotalLandMassBox = () => import('@/components/molecules/TotalLandMassBox.vue')
const EyeCatchBanner = () => import('@/components/molecules/EyeCatchBanner.vue')
const PpdLimitedSaleBelt = () => import('@/components/molecules/PpdLimitedSaleBelt.vue')
const UpDownPricePicker = () => import('@/components/organisms/UpDownPricePicker.vue')
const TwoButtonBpcBuyer = () => import('@/components/organisms/TwoButtonBpcBuyer.vue')
const BuyConfirmationModal = () => import('@/components/molecules/BuyConfirmationModal.vue')

// Nuxt
const nuxtApp = useNuxtApp()
const { nuxt2Context, $toast, $t }: any = nuxtApp
const { $landMakeService, $constants, $cryptoCoinService, $ppdService } = nuxt2Context

// State
const LAND_VOLUME_PROCESS_TYPE = 4
const buyAmount = ref(1)
const userBpc = ref(0)
const userBpcp = ref(0)
const pricePerUnit = ref(300)
const isBuying = ref(false)
const processId = ref(0)
const processCount = ref(0)
const ppdLimitedSaleStatusInfos = ref([])
const buyMode = ref('')
const isBuyConfirmationModalShown = ref(false)
let nonce = 0
let contract: any = {}
const banner = LandVolumeBanner

onBeforeMount(async () => {
  await fetch()
})

// Computed
const isBpcBuyable = computed(() => {
  if (!useAuthStore().getNetworkBool.isHomeverse) {
    return false
  }

  if (processingTransaction.value) {
    return false
  }

  return userBpc.value >= buyAmount.value * pricePerUnit.value
})

const isBpcpBuyable = computed(() => {
  if (processingTransaction.value) {
    return false
  }

  return userBpcp.value >= buyAmount.value * pricePerUnit.value
})

const totalPrice = computed(() => {
  return buyAmount.value * pricePerUnit.value
})

const processingTransaction = computed(() => {
  return isBuying.value || processId.value != 0 || processCount.value > 0
})

const canBuyPpdLimitedLandVolumeSale = computed(() => {
  return !ppdLimitedSaleStatusInfos.value.find(info => info.recordType == RecordType.SPENDPPDLANDVOLUMEPURCHASE)?.status
})

// Method
async function fetch() {
  ppdLimitedSaleStatusInfos.value = await $ppdService.getUserPpdRecordStatusInfos()
  await useConsumableStore().fetchConsumeMyItems()
  userBpcp.value = useConsumableStore().myBalance.bpcPoint

  if (useAuthStore().getNetworkBool.isHomeverse) {
    const web3 = await useWalletStore().getWeb3
    contract = new web3.eth.Contract(ItemPurchaseAbi as any, useAddressStore().getItemPurchaseAddress)
    nonce = await contract.methods.nonces(useAuthStore().getEthAddress).call()
    await useAddressStore().fetchBpcBalance()
    userBpc.value = useAddressStore().getBpcBalance
  }

  processCount.value = await $cryptoCoinService.GetUserPurchaseProcessCount($constants.PURCHASE_PROCESS_TYPE.ITEM)
}

async function buy() {
  processCount.value = await $cryptoCoinService.GetUserPurchaseProcessCount($constants.PURCHASE_PROCESS_TYPE.ITEM)
  if (processingTransaction.value) {
    $toast.error($t('common.processingTransaction'))
    return
  }
  isBuying.value = true
  isBuyConfirmationModalShown.value = false

  try {
    if (buyMode.value == 'bpcp') {
      await $landMakeService.buyLandVolume(buyAmount.value)
    } else {
      const sig = await $cryptoCoinService.GetItemPurchaseSignature(
        totalPrice.value,
        LAND_VOLUME_PROCESS_TYPE,
        buyAmount.value,
        useAddressStore().getBPCAddress
      )
      processId.value = sig.processId // 失敗したらこのプロセスを強制的に閉じる
      const web3 = await useWalletStore().getWeb3
      const bigIntPrice = web3.utils.toWei(totalPrice.value.toString(), 'ether')

      // ユーザーの許可Approve、一回のみ
      const tokenContract = new web3.eth.Contract(FtAbi as any, useAddressStore().getBPCAddress)
      const allowance = await tokenContract.methods
        .allowance(useAuthStore().getEthAddress, useAddressStore().getItemPurchaseAddress)
        .call()
      if (BigInt(allowance) < BigInt(bigIntPrice)) {
        await tokenContract.methods
          .approve(useAddressStore().getItemPurchaseAddress, useAddressStore().getBigIntMax)
          .send({ from: useAuthStore().getEthAddress })
      }

      // BPCで購入
      await contract.methods
        .purchase(
          nonce,
          sig.processId,
          bigIntPrice,
          LAND_VOLUME_PROCESS_TYPE,
          buyAmount.value,
          useAddressStore().getBPCAddress,
          sig.multiSignature.sigv,
          sig.multiSignature.sigr,
          sig.multiSignature.sigs
        )
        .send({ from: useAuthStore().getEthAddress })

      // トランザクション確認
      await $cryptoCoinService.UserCheckPurchaseProcess(processId.value, $constants.PURCHASE_PROCESS_TYPE.ITEM)
    }

    await fetch()
    isBuying.value = false
    processId.value = 0
    buyAmount.value = 1
    $toast.success('Success!')
  } catch (e) {
    if (processId.value != 0 && e.code == 4001) {
      // 強制的に閉じる
      await $cryptoCoinService.ForceCloseItemPurchaseProcess(processId.value)
    }
    isBuying.value = false
    processId.value = 0
    $toast.error(e.message)
    console.error(e)
  }
}

function buyLandVolume(mode: string) {
  buyMode.value = mode
  isBuyConfirmationModalShown.value = true
}

function updateAmount(amount: number) {
  buyAmount.value = amount
}
</script>

<template>
  <div>
    <div v-if="isBuying" class="grid place-items-center mt-4">
      <fa-icon :icon="['fal', 'spinner']" spin size="3x" />
    </div>
    <div v-else class="grid place-items-center">
      <EyeCatchBanner :banner="banner" class="mt-1" />
      <PpdLimitedSaleBelt
        v-if="canBuyPpdLimitedLandVolumeSale"
        :RecordType="RecordType.SPENDPPDLANDVOLUMEPURCHASE"
        class="my-1"
      />
      <TotalLandMassBox class="mt-1" :hasRedirectButton="false" />
      <UpDownPricePicker :pricePerUnit="pricePerUnit" @update="updateAmount" class="my-5" />
      <TwoButtonBpcBuyer
        :price="totalPrice"
        :isBpcBuyable="isBpcBuyable"
        :isBpcpBuyable="isBpcpBuyable"
        :isProcessing="processingTransaction"
        @buyWithBpc="buyLandVolume('bpc')"
        @buyWithBpcp="buyLandVolume('bpcp')"
      />
    </div>
    <BuyConfirmationModal
      v-if="isBuyConfirmationModalShown"
      @modal-close="isBuyConfirmationModalShown = false"
      :mode="buyMode"
      @buy="buy"
      :obj="{ amount: buyAmount, price: totalPrice, name: this.$t('land.landMake.landVolume'), onSale: 0 }"
      :isDuplicationError="false"
    />
  </div>
</template>
